import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import addDays from 'date-fns/addDays';
import DateInput from 'products/nomad_insurance/components/DateInput';
import Button from 'products/nomad_insurance/components/Button';
import TextInput from 'products/nomad_insurance/components/TextInput';
import Select from 'products/nomad_insurance/components/Select';
import useIsNI2 from 'products/nomad_insurance/hooks/useIsNI2';
import { checkIfUserIsAdult } from 'products/nomad_insurance/helpers';
import { COUNTRY_OPTIONS } from 'products/nomad_insurance/components/Select/consts';
import {
  StyledAlert,
  StyledInputFieldAlert,
  StyledFeedback,
  Paragraph,
  WarningHeading,
  StyledInputSection,
  InputSectionLabel,
  InputSectionDescription,
  InputSectionChildren,
  NameInputs,
  FormWrapper,
  Buttons
} from './styled';
import validation from './validation';

const InputSection = ({ label, description, children, hideTopBorder }) => {
  return (
    <StyledInputSection $hideTopBorder={hideTopBorder}>
      <div>
        <InputSectionLabel>{label}</InputSectionLabel>
        {description && <InputSectionDescription>{description}</InputSectionDescription>}
      </div>
      <InputSectionChildren>{children}</InputSectionChildren>
    </StyledInputSection>
  );
};

const DuplicateMemberAlert = () => {
  return (
    <StyledInputFieldAlert variant="warning" label="Duplicate member">
      It looks like you're adding yourself as a group member. You are already covered as
      the policyholder, so you don't need to add yourself again. If you're travelling
      solo, you can skip this step.
    </StyledInputFieldAlert>
  );
};

InputSection.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node.isRequired,
  hideTopBorder: PropTypes.bool
};

const ApplicantForm = ({
  onSubmit,
  groupMember,
  hasUSCoverage,
  planholder,
  onCancel,
  hideFirstTopBorder,
  homeCountry
}) => {
  const dateInputFormRef = useRef(null);
  const [showUSError, setShowUSError] = useState(false);
  const { hasEndDate, startDate, endDate } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );
  const { applicants } = useSelector((state) => state.nomadInsurance.application);

  const handleSave = (groupMemberInfo) => {
    if (groupMemberInfo?.citizenship === 'US' && hasUSCoverage) {
      setShowUSError(true);
      return;
    }
    onSubmit(groupMemberInfo);
    handleClose(); // eslint-disable-line
  };

  const initialValues = useMemo(
    () => ({
      firstName: groupMember?.firstName || '',
      lastName: groupMember?.lastName || '',
      email: groupMember?.email || '',
      dateOfBirth: groupMember?.dateOfBirth || '',
      citizenship: groupMember?.citizenship || '',
      homeCountry: homeCountry || groupMember?.homeCountry || '',
      gender: groupMember?.gender.toLowerCase() || ''
    }),
    [groupMember]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: validation,
    onSubmit: handleSave,
    validateOnMount: true,
    enableReinitialize: true
  });

  useEffect(() => {
    // manually validate the form when values change
    // this is because on mobile, otherwise the validation happens only on blur
    // which looks like the field you just filled in is not valid until you click away
    formik.validateForm();
  }, [formik.values]);

  useEffect(() => {
    formik.setValues(initialValues);
  }, [initialValues]);

  const handleClose = () => {
    formik.resetForm();
    dateInputFormRef.current.resetForm();
  };

  const isGroupMemberAdult = useMemo(() => {
    const { dateOfBirth } = formik.values;
    if (!dateOfBirth) {
      return false;
    }
    return checkIfUserIsAdult(
      dateOfBirth,
      hasEndDate ? endDate : addDays(new Date(startDate), 363)
    );
  }, [formik.values.dateOfBirth]);

  const shouldRenderAdulthoodWarning = isGroupMemberAdult;

  const isGroupMemberMinor = useMemo(() => {
    return formik.values.dateOfBirth && !isGroupMemberAdult;
  }, [formik.values.dateOfBirth, isGroupMemberAdult]);

  const isPlanholderNameDuplicate = useMemo(() => {
    return (
      formik.values.firstName === planholder?.firstName &&
      formik.values.lastName === planholder?.lastName
    );
  }, [planholder, formik]);

  const isPlanholderDOBDuplicate = useMemo(() => {
    return formik.values.dateOfBirth === planholder?.dateOfBirth;
  }, [planholder, formik]);

  useEffect(() => {
    if (isGroupMemberMinor) {
      formik.setFieldValue('email', planholder?.email);
    }
  }, [isGroupMemberMinor]);

  return (
    <FormWrapper>
      {showUSError && (
        <StyledAlert variant="danger" label="Cannot add group member">
          You have selected the US addon, therefore you cannot add a US citizen as a group
          member.
        </StyledAlert>
      )}
      {shouldRenderAdulthoodWarning && (
        <StyledFeedback variant="warningOrange" topAlignIcon>
          <WarningHeading>Heads up about adding adult group members!</WarningHeading>
          <Paragraph>
            Please note that our digital claims process is only accessible from the
            primary member's user dashboard. This means when you add adult group members,
            medical information, banking details, claims updates and other information
            about you and your group members will only be accessible through this account.
          </Paragraph>
          <Paragraph>
            If you'd like each adult group member to manage their own claims, we recommend
            creating separate accounts for each adult and purchasing separately. Don't
            worry - there's no price difference!
          </Paragraph>
        </StyledFeedback>
      )}

      <InputSection label="Full name" hideTopBorder={hideFirstTopBorder}>
        <NameInputs>
          <TextInput
            label="First name"
            name="firstName"
            formik={formik}
            variant="white"
          />
          <TextInput label="Last name" name="lastName" formik={formik} variant="white" />
        </NameInputs>
        {isPlanholderNameDuplicate && <DuplicateMemberAlert />}
      </InputSection>

      <InputSection label="Date of birth">
        <DateInput
          name="dateOfBirth"
          formik={formik}
          ref={dateInputFormRef}
          variant="white"
        />
        {isPlanholderDOBDuplicate && <DuplicateMemberAlert />}
      </InputSection>

      <InputSection label="Biological sex">
        <Select
          label="Biological sex"
          name="gender"
          formik={formik}
          variant="white"
          options={[
            { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' }
          ]}
          showIndicator
        />
      </InputSection>

      <InputSection label="Citizenship">
        <Select
          showFlag
          label="Citizenship"
          name="citizenship"
          formik={formik}
          options={COUNTRY_OPTIONS}
          variant="white"
          showIndicator
        />
      </InputSection>

      <InputSection
        label="Home country"
        description="Where you principally reside and receive regular mail."
      >
        <Select
          showFlag
          label="Home country"
          name="homeCountry"
          formik={formik}
          options={COUNTRY_OPTIONS}
          variant="white"
          disabled
        />
      </InputSection>

      <InputSection
        label="Email"
        description={
          isGroupMemberMinor
            ? 'For members under 18, communications will be done through the main policyholder.'
            : ''
        }
      >
        <TextInput
          label="Email"
          name="email"
          formik={formik}
          variant="white"
          disabled={isGroupMemberMinor}
        />
      </InputSection>

      <Buttons>
        <Button
          variant="textOnly"
          onClick={() => {
            handleClose();
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          color="fountainBlue"
          onClick={formik.submitForm}
          disabled={!formik.isValid || !formik.dirty || applicants.length === 10}
        >
          {groupMember ? 'Edit member' : 'Add member'}
        </Button>
      </Buttons>
    </FormWrapper>
  );
};

ApplicantForm.propTypes = {
  onSubmit: PropTypes.func,
  groupMember: PropTypes.object,
  hasUSCoverage: PropTypes.bool,
  planholder: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  hideFirstTopBorder: PropTypes.bool,
  homeCountry: PropTypes.string
};

export default ApplicantForm;
